import {
  Button,
  ButtonGroup,
  closeDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetClosingDayQuery, useUpdatePublicClosingDayMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {getOptionsFromBranches, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {CLOSING_DAY_DIALOG_ID, CLOSING_DAY_GRID_CODE} from '../utils/constants';
import {UpdatePublicClosingDayFormValues, updatePublicClosingDaySchema} from '../utils/validations';

const handleClose = () => closeDialog(CLOSING_DAY_DIALOG_ID);

type PublicClosingDayModalProps = {
  closingDayId: string;
};

export function PublicClosingDayModal(props: PublicClosingDayModalProps) {
  const {data: branchList, isLoading: isBranchListLoading} = useGetBranchListQuery();
  const {data: closingDay, isLoading: isClosingDayLoading} = useGetClosingDayQuery({
    closingDayId: props.closingDayId,
  });
  const [updatePublic] = useUpdatePublicClosingDayMutation();

  const branchOptions = getOptionsFromBranches(branchList);

  const handleSubmit: FormSubmitHandler<UpdatePublicClosingDayFormValues> = (values) =>
    updatePublic({
      closingDayId: props.closingDayId,
      branchIds: values.branchIds,
      enabled: values.enabled,
    })
      .unwrap()
      .then(() => {
        refreshDmsDatagrid(CLOSING_DAY_GRID_CODE);
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        handleClose();
      })
      .catch(handleApiError);
  return (
    <VStack spacing={4}>
      <VStack spacing={1}>
        <Text size="xSmall" color="secondary">
          {i18n.t('general.labels.date')}
        </Text>
        <Text>{closingDay?.date}</Text>
      </VStack>
      <VStack spacing={1}>
        <Text size="xSmall" color="secondary">
          {i18n.t('general.labels.name')}
        </Text>
        <Text>{closingDay?.localName}</Text>
      </VStack>
      <DataStatus isLoading={isBranchListLoading || isClosingDayLoading}>
        <Form
          defaultValues={{
            branchIds: closingDay?.branchIds,
            enabled: closingDay?.enabled,
          }}
          experimentalZodSchema={updatePublicClosingDaySchema}
          onSubmit={handleSubmit}
        >
          {(control) => (
            <VStack spacing={4}>
              <FormField
                type="multiChoice"
                control={control}
                name="branchIds"
                label={i18n.t('entity.branch.labels.branches')}
                options={branchOptions}
                menuInPortal
              />
              <FormField
                type="checkbox"
                control={control}
                name="enabled"
                label={i18n.t('general.labels.enabled')}
              />
              <Separator spacing={2} />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={handleClose}
                />
                <FormButton
                  control={control}
                  title={i18n.t('general.actions.save')}
                  type="submit"
                  variant="primary"
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </VStack>
  );
}
